import {
	Box,
	ClickAwayListener,
	OutlinedInput,
	styled,
	Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { Close, NorthEast, SearchRounded } from '@mui/icons-material'
import { search } from '@/api'
import { useRequest } from 'ahooks'
import classNames from 'classnames';
import Link from 'next/link'
import PulseLoader from 'react-spinners/PulseLoader'
import { usePathname } from 'next/navigation'
import { useRouter } from 'next/router'

const RawInput = styled(OutlinedInput)(({ theme }) => ({
	borderColor: theme.palette.common.borderPrimary,
	backgroundColor: 'transparent',
	height: 40,
	paddingLeft: 16,
	paddingRight: 16,
	borderRadius: 0,
	fontWeight: 400,
	fontSize: 15,
	lineHeight: '30px',
	'&.Mui-focused, &:hover': {
		borderColor: theme.palette.common.borderPrimary,
		color: theme.palette.common.textPrimary,
	},
	'&.Mui-focused': {
		backgroundColor: theme.palette.common.backgroundPrimary,
	},
}))

export default function SearchInput() {
	const [query, setQuery] = useState<string>('')
	const [results, setResults] = useState<SearchResponse[] | null>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	const { run, loading } = useRequest((query) => search(query), {
		manual: true,
		debounceWait: 300,
		onSuccess(data: any) {
			setResults(data);
		},
	});
	const pathname = usePathname();
	const router = useRouter();

	useEffect(() => {
		setResults(null);
		if (!query) {
			return;
		}
		run(query);
	}, [query])
	useEffect(() => setQuery(''), [pathname])

	return (
		<ClickAwayListener onClickAway={() => setQuery('')}>
			<Box className='relative'>
				<RawInput
					inputRef={inputRef}
					value={query}
					onChange={(e) => {
						setQuery(e.target.value)
					}}
					fullWidth
					placeholder='Search by address / transaction'
					startAdornment={<SearchRounded onClick={() => run(query)} sx={{ mr: 2 }} />}
					endAdornment={query && <Close onClick={() => setQuery('')} className={classNames('bg-N450 rounded-full p-px cursor-pointer')} fontSize='small' sx={{ color: (theme) => theme.palette.common.backgroundPrimary }} />}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							if (results && results.length !== 0) {
								let res = results[0];
								if (res.value === query) {
									router.push(res.type === 'TX' ? `/tx/${res.orderId}` : `/trader/${res.value}`);
									e.preventDefault();
									return;
								}
							}
							run(query)
						}
					}}
				/>
				{query && <Box className={classNames('border border-solid border-borderPrimary bg-backgroundPrimary mt-1 absolute left-0 right-0 z-10')}>
					{loading ? (
						<Box className={classNames('text-center py-4')}>
							<PulseLoader color='#ffffff' size={6} speedMultiplier={0.5} />
						</Box>
					) : (results &&
						<>
							<Typography variant='appTinyRegular' className={classNames('mt-4 ml-12 block')}>
								Related results
							</Typography>
							{results?.map((res) => (
								<Link key={res.value} className={classNames('my-3 ml-3 block')} href={res.type === 'TX' ? `/tx/${res.orderId}` : `/trader/${res.value}`}>
									<NorthEast fontSize='small' />
									<Typography variant='appSmallRegular' color="common.textPrimary" className={classNames('mx-3')}>
										{trimTx(res.value)}
									</Typography>
								</Link>
							))}
							{results?.length === 0 &&
								<Box className={classNames('my-3 mx-3 block')}>
									<Typography variant='appSmallRegular' color="common.textPrimary" className={classNames('mx-3')}>
										Transaction or address not found.
									</Typography>
								</Box>
							}

						</>
					)}
				</Box>}
			</Box>
		</ClickAwayListener>
	)
}

function trimTx(tx: string): string {
	if (tx.length <= 32) {
		return tx;
	}
	return tx.slice(0, 16) + '...' + tx.slice(tx.length - 16);
}
